<template>
  <div>
    <div class="d-flex mb-3">
      <back-button v-if="backTo" :to="backTo" class="mr-auto" />

      <slot :newItem="newItem" name="header">
        <template v-if="$gate.allow('create', modelType)">
          <b-button
              :disabled="newItem.busy"
              class="text-nowrap ml-auto"
              variant="primary"
              @click="showCreateModal = !showCreateModal"
          >
            {{ $t(`Create new ${modelType}`) }}
            <fa class="ml-1" icon="plus" />
          </b-button>

          <b-modal
              v-model="showCreateModal"
              :ok-title="$t('Save')"
              :title="$t(`Create new ${modelType}`)"
          >
            <b-form
                ref="newItemForm"
                @keydown="newItem.onKeydown($event)"
                @submit.prevent="handleCreate(false)"
            >
              <b-form-group
                  :invalid-feedback="newItem.errors.get('name')"
                  :label="$t('Name')"
                  :state="newItem.errors.has('name') ? false : null"
                  label-for="newItemName"
              >
                <b-form-input
                    id="newItemName"
                    v-model="newItem.name"
                    :state="newItem.errors.has('name') ? false : null"
                    autofocus
                    maxlength="255"
                    required
                />
              </b-form-group>

              <b-form-group
                  :invalid-feedback="newItem.errors.get('short_name')"
                  :label="$t('Short name')"
                  :state="newItem.errors.has('short_name') ? false : null"
                  label-for="newItemShortName"
              >
                <b-form-input
                    id="newItemShortName"
                    v-model="newItem.short_name"
                    :state="newItem.errors.has('short_name') ? false : null"
                    maxlength="5"
                />
              </b-form-group>

              <slot name="newItemFormAppend" :newItem="newItem" />

              <input class="d-none" type="submit">
            </b-form>

            <template #modal-footer>
              <b-dropdown :text="$t('Save')" split variant="primary" @click="handleCreate(false)">
                <b-dropdown-item @click="handleCreate(true)">
                  {{ $t('Save and new') }}
                </b-dropdown-item>
              </b-dropdown>
            </template>
          </b-modal>
        </template>
      </slot>
    </div>

    <b-card header-class="d-flex align-items-center justify-content-between flex-wrap" no-body>
      <template #header>
        <span class="mr-3"> {{ header }}</span>

        <search-input v-model="search" />
      </template>

      <b-list-group v-if="!pending" flush>
        <slot :items="filteredItems">
          <b-list-group-item
              v-for="item in filteredItems"
              :key="item.id"
              class="justify-between"
          >
            <b-link
                :to="listItemUrl || item.url"
                class="flex-grow-1"
            >
              {{ item.name }}
              <small v-if="item.short_name" class="d-block">{{ item.short_name }}</small>
            </b-link>

            <list-item-buttons
                :api-url="apiUrl"
                :item="item"
                :show-clone-options="showCloneOptions"
                hide-locations
                @deleted="items.splice(items.indexOf($event), 1)"
                @duplicated="items.push($event)"
            />
          </b-list-group-item>
        </slot>
      </b-list-group>

      <no-results-found v-if="!pending && !items.length" />

      <spinner v-if="pending || newItem.busy" />
    </b-card>

    <b-pagination
        v-if="showPagination"
        v-model="currentPage"
        :disabled="pending"
        :per-page="perPage"
        :total-rows="totalRows"
        align="fill"
        limit="3"
    />
  </div>
</template>

<script>
import Spinner from "@/components/Spinner";
import ListItemButtons from "@/components/ListItemButtons";
import fetchPaginatedData from "@/mixins/fetchPaginatedData";
import crudListener from "@/mixins/crudListener";
import Form from "vform";
import SearchInput from "@/components/SeachInput";

export default {
  name: "SidebarItemsList",
  components: {SearchInput, ListItemButtons, Spinner},
  mixins: [fetchPaginatedData, crudListener],
  props: {
    apiUrl: {
      type: String,
      required: true
    },
    backTo: {
      type: Object,
      default: null
    },
    header: {
      type: String,
      required: true
    },
    listItemUrl: {
      type: [String, Object],
      default: null
    },
    modelType: {
      type: String,
      required: true
    },
    showCloneOptions: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      newItem: new Form(),
      showCreateModal: false
    };
  },
  computed: {
    filteredItems() {
      if (this.showPagination || this.externalSearch) {
        return this.items;
      }

      if (!this.search) {
        return this.items;
      }

      return this.items.filter(
          ({name, short_name}) => (name && name.includes(this.search) || short_name && short_name.includes(this.search))
      );
    }
  },
  methods: {
    async handleCreate(saveAndNew = false) {
      try {
        let item = await this.newItem.post(this.apiUrl).then(({data}) => data);

        this.savedToast();

        this.items.push(item);

        if (this.$refs.newItemForm && this.$refs.newItemForm.reset) {
          this.$refs.newItemForm.reset();
        }

        this.newItem.reset();

        if (!saveAndNew) {
          this.showCreateModal = false;
        }
      } catch (e) {
        if (!e.response) throw e;
      }
    },

    listenForWebsocketEvents() {
        this.$echo.channel(this.modelType)
            .listen('.DuplicationCompleted', this.fetchData);
    },
  },
}
</script>
